// Manager "Ask Maeve" frontdoor — a global entry point into Manager Sub-mode
// B guidance that doesn't require the manager to already be inside a case.
//
// Manager Maeve CONVERSATIONS are always audit-logged against a specific
// case's `employeeId` for CGC review (see `services/maeve.ts`
// `respondToManagerGuidance` and `renderCaseBlock`), so there is no case-less
// "just ask Maeve anything" backend mode — unlike HR's org-wide
// `/hr/ask-maeve`. This page keeps that governance model intact: it's a picker
// ("who's this about?") in front of the existing, already-CGC-gated
// `/api/cases/:id/maeve/guidance` endpoint, not a new prompt surface.
//
// `/manager/draft` (Sub-mode A′, PO 2026-08-06) is NOT a counter-example, and
// the difference is worth being precise about because it is the reason that
// one was allowed to exist: it is a one-shot form whose whole request body is
// two closed enums, so it cannot carry an identity and there is no multi-turn
// transcript to review — it audits with `employeeId: null`. A free-text chat
// with no case attached would be neither, and still has no backend mode.
//
// ONE LAYOUT, PURE CSS BREAKPOINTS (PO 2026-07-31). `ManagerMaeveChat` is the
// only implementation at every width: full-bleed inside the bottom-nav app
// shell below `md`, and a bounded, bordered column at `md` and above. This
// page renders a CONSTANT element tree and switches nothing in JS.
//
// That constraint is load-bearing, not stylistic. An earlier revision branched
// on `useMediaQuery` and returned two different trees. Because a media query
// is live, rotating a phone to landscape (an iPhone 14 Pro Max is 932px wide
// that way — well past `md`) crossed the breakpoint mid-conversation, React
// unmounted the chat, and the session-only guidance history went with it; if a
// turn was in flight, its reply landed on a stale closure and vanished
// silently — the exact failure `backDisabled`/`onPendingChange` exist to
// prevent elsewhere. The retired UA-gated route never had this problem
// because a User-Agent doesn't change when you rotate the device. Keep the
// tree constant: any layout difference here belongs in a `md:` class, never
// in a JS branch.
//
// A previous document-style card layout for desktop (a second picker plus
// `ManagerMaeveGuidance`) was deleted rather than kept behind a branch — it
// duplicated the picker and was the thing forcing the branch to exist.
// `ManagerMaeveGuidance` itself is untouched and still serves the in-case
// surfaces (`ManagerCaseDetail`, `ManagerCheckIn`).

import { useSession } from '@/lib/auth.ts';
import { firstNameFromSession } from '@/lib/format.ts';
import { ManagerMaeveChat } from '@/components/ManagerMaeveChat.tsx';

export function ManagerAskMaeve() {
  const session = useSession('manager-shell');
  const firstName = firstNameFromSession(session as { name?: string });

  return (
    <>
      {/* Page heading for the desktop document layout. Hidden below `md`,
          where the chat's own header (Maeve · Online · MANAGER) is the
          heading and a second one would be redundant chrome — the PO's
          "minimum chrome" direction. */}
      <header className="hidden md:mb-4 md:block">
        <h1 className="text-xl font-semibold">Ask Maeve</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Policy questions, what-to-say, what-to-do-next — pick which support pathway this is about
          and Maeve will ground her answer in that pathway&rsquo;s context.
        </p>
      </header>

      {/* `h-full` below `md`: `ManagerShell` gives this route a full-bleed,
          non-scrolling content wrapper, itself a flex child with a definite
          height, so the chat fills it exactly and scrolls internally. At `md`+
          it becomes a bounded column inside the normal padded page — `dvh`
          (not `vh`) so mobile-browser URL-bar collapse can't leave the
          composer under the fold on a large-phone landscape viewport, which
          lands in the `md`+ layout rather than the phone one. */}
      <div className="h-full overflow-hidden md:h-[70dvh] md:min-h-[26rem] md:rounded-lg md:border md:border-border">
        <ManagerMaeveChat firstName={firstName} />
      </div>
    </>
  );
}
